'use client'

import { useEffect } from 'react'
import { trackCtaClick } from '@/lib/analytics/cta'

/**
 * Sitewide primary-CTA instrumentation: one delegated click listener that
 * reports `cta_click` for any anchor carrying — or nested inside an element
 * carrying — `data-cta-id`.
 *
 * Renders nothing and changes no behavior: server components stay server,
 * links navigate exactly as before, and a native click also covers keyboard
 * activation. Replaces the homepage-only `HomeCtaTracker`, which left the
 * `data-cta-id` markers already present on /projects, /platform/sample-report,
 * /contact-us and the sitewide header firing nothing at all.
 *
 * Mounted inside ConditionalMarketingShell, so it covers every marketing
 * surface (including /platform and /platform/sample-report) and never boots
 * inside the authenticated app at /platform/app — the same boundary the
 * Clarity loader honors.
 *
 * The listener is registered once for the lifetime of the shell and reads
 * everything it needs from the event target, so client-side navigation never
 * needs to re-bind it or hand it a stale route.
 */
export default function CtaTracker() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null
      const anchor = target?.closest?.('a[href]')
      if (!anchor) return
      // closest() starts at the anchor itself, so a data-cta-id set directly
      // on the link is picked up alongside a wrapping carrier.
      const carrier = anchor.closest<HTMLElement>('[data-cta-id]')
      if (!carrier) return
      trackCtaClick(
        carrier.dataset.ctaId as string,
        anchor.getAttribute('href')
      )
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [])

  return null
}
